import React, { useEffect } from "react";
import confetti from "canvas-confetti";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Full-screen celebratory overlay shown when a sale is released.
 * Triggers a confetti burst on mount and fades in a "Great Job!" card.
 */
export default function ReleaseCelebration({ open, srNumber, onViewSale, onClose }) {
  useEffect(() => {
    if (!open) return;
    // Confetti burst — two cannons from each side
    const duration = 1800;
    const end = Date.now() + duration;
    const colors = ["#1f4ed8", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];
    (function frame() {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 70,
        startVelocity: 55,
        origin: { x: 0, y: 0.7 },
        colors,
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 70,
        startVelocity: 55,
        origin: { x: 1, y: 0.7 },
        colors,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();

    // Center burst
    confetti({
      particleCount: 120,
      spread: 100,
      startVelocity: 40,
      origin: { x: 0.5, y: 0.6 },
      colors,
    });
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 px-4 backdrop-blur-sm"
      data-testid="release-celebration"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/40 bg-white p-8 text-center shadow-2xl"
        style={{ animation: "od360-pop 360ms cubic-bezier(.2,1.4,.4,1) both" }}
      >
        <style>{`
          @keyframes od360-pop {
            0% { transform: scale(.85) translateY(8px); opacity: 0; }
            100% { transform: scale(1) translateY(0); opacity: 1; }
          }
          @keyframes od360-checkdraw {
            0% { stroke-dashoffset: 48; }
            100% { stroke-dashoffset: 0; }
          }
          @keyframes od360-ringpulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,.35); }
            50% { box-shadow: 0 0 0 16px rgba(16,185,129,0); }
          }
        `}</style>

        {/* Animated check */}
        <div
          className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500"
          style={{ animation: "od360-ringpulse 1.6s ease-out 1" }}
        >
          <svg viewBox="0 0 52 52" className="h-12 w-12">
            <circle cx="26" cy="26" r="24" fill="none" stroke="white" strokeOpacity="0.3" strokeWidth="3" />
            <path
              d="M14 27 l8 8 l16 -16"
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="48"
              strokeDashoffset="48"
              style={{ animation: "od360-checkdraw 540ms ease-out 200ms forwards" }}
            />
          </svg>
        </div>

        <div className="mt-5 text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-600">
          Sale Released
        </div>
        <h2 className="mt-1 font-display text-3xl font-semibold tracking-tight text-slate-900">
          Great Job! 🎉
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          The sale has been released and PDI / Accounts / Spares tasks are now in their queues.
        </p>

        <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
          <span className="font-mono-data text-sm font-semibold text-emerald-800">
            {srNumber}
          </span>
        </div>

        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
          <Button variant="outline" onClick={onClose} data-testid="celebration-close-btn">
            Close
          </Button>
          <Button onClick={onViewSale} className="gap-1.5" data-testid="celebration-view-btn">
            View Sale <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
